import { useCallback } from 'react'

import { Typography } from '@mui/material'
import { GridColDef, GridRowParams } from '@mui/x-data-grid'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router'

import { CursorPaginatedTable } from 'app/lib/components/table'
import TableContent from 'app/lib/components/TableContent'
import { DEFAULT_PAGE_LIMIT } from 'app/lib/constants'
import { userProfile } from 'app/lib/routes'
import { formatDate } from 'app/lib/utils/date'
import { generateQueryParamsFromSearchQuery } from 'app/lib/utils/helpers'
import { GetUsersParams } from 'app/models/User'
import { useGetUsersQuery } from 'app/redux/scribeApi'

export const Table = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const { t: tHeader } = useTranslation(undefined, {
    keyPrefix: 'usersList.table',
  })

  const {
    t,
    i18n: { resolvedLanguage = 'en' },
  } = useTranslation()

  const { search, cursor } = Object.fromEntries(searchParams.entries()) as GetUsersParams
  const queryParams = generateQueryParamsFromSearchQuery(search || '')
  const { data, isFetching } = useGetUsersQuery({ cursor, ...queryParams })

  const records = data?.data || []
  const pagination = {
    nextCursor: data?.meta?.nextCursor,
    prevCursor: data?.meta?.prevCursor,
  }

  const onFetchData = useCallback(
    (csr?: string) => {
      const params: Record<string, string> = {}
      if (search) params.search = search
      if (csr) params.cursor = csr
      setSearchParams(params)
    },
    [setSearchParams, search],
  )

  const columns: GridColDef[] = [
    {
      field: 'userId',
      headerName: tHeader('userId'),
      width: 275,
    },
    {
      field: 'firstName',
      headerName: tHeader('firstName'),
      flex: 0.5,
      renderCell: (params) => <Typography variant="subtitle2">{params.value}</Typography>,
    },
    {
      field: 'lastName',
      headerName: tHeader('lastName'),
      flex: 0.5,
      renderCell: (params) => <Typography variant="subtitle2">{params.value}</Typography>,
    },
    {
      field: 'dateOfBirth',
      headerName: tHeader('dateOfBirth'),
      flex: 0.5,
      renderCell: (params) => (params.value ? formatDate(params.value, resolvedLanguage) : ''),
    },
    {
      field: 'email',
      headerName: tHeader('email'),
      flex: 1,
    },
    {
      field: 'status',
      headerName: tHeader('status'),
      width: 120,
    },
  ]

  const rows = records.map(({ id, firstName, lastName, dateOfBirth, email, authId }) => ({
    id,
    userId: id,
    firstName,
    lastName,
    dateOfBirth,
    email,
    status: authId ? t('userProfile.info.signedUp') : t('userProfile.info.notSignedUp'),
  }))

  const handleRowClick = (params: GridRowParams) => {
    navigate(userProfile.get(params.row.userId))
  }

  return (
    <TableContent
      isLoading={isFetching}
      count={records.length}
      notFound={t('usersList.noResults.noUsersFound')}
      searchNoMatch={t('usersList.noResults.searchNoMatch')}
      iconName="no_member_found"
    >
      <CursorPaginatedTable
        key={search}
        rows={rows}
        columns={columns}
        limit={DEFAULT_PAGE_LIMIT}
        pagination={pagination}
        onFetchData={onFetchData}
        onRowClick={handleRowClick}
        testID="user-list-table"
      />
    </TableContent>
  )
}
